import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import { Box, MainText, MainTitle } from "app/components/common";
import theme from "app/Theme/Theme";
import React, { useMemo } from "react";
import { Animated, Platform, View } from "react-native";
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from "react-native-confirmation-code-field";
import styles from "./styles";

const { Value } = Animated;

const CELL_COUNT = 8;
const HIT_SLOP = { top: 20, bottom: 20, left: 20, right: 20 };

const getAnimationsScale = cellCount =>
  [...new Array(cellCount)].map(() => new Value(0));
const getAnimationsColor = cellCount =>
  [...new Array(cellCount)].map(() => new Value(1));

const animateCell = ({
  animationsColor,
  animationsScale,
  hasValue,
  index,
  isFocused,
}) => {
  Animated.parallel([
    Animated.timing(animationsColor[index], {
      useNativeDriver: false,
      toValue: isFocused ? 1 : 0,
      duration: 250,
    }),
    Animated.spring(animationsScale[index], {
      useNativeDriver: false,
      toValue: hasValue ? 1 : 0,
      duration: hasValue ? 300 : 250,
    }),
  ]).start();
};

const AnimatedExample = ({ value, setValue, style }) => {
  const ref = useBlurOnFulfill({ value, CELL_COUNT });
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue,
  });

  const animationsScale = useMemo(() => getAnimationsScale(CELL_COUNT), [
    CELL_COUNT,
  ]);
  const animationsColor = useMemo(() => getAnimationsColor(CELL_COUNT), [
    CELL_COUNT,
  ]);

  const renderCell = ({ index, symbol, isFocused }) => {
    const hasValue = Boolean(symbol);

    const animatedCellStyle = {
      backgroundColor: animationsScale[index].interpolate({
        inputRange: [0, 1],
        outputRange: [theme.colors["neutral.75"], theme.colors["brand.500"]],
      }),
      color: animationsScale[index].interpolate({
        inputRange: [0, 1],
        outputRange: [theme.colors["brand.500"], theme.colors["neutral.white"]],
      }),
      borderRadius: animationsScale[index].interpolate({
        inputRange: [0, 1],
        outputRange: [isWebDesktop ? 8 : 4, 8],
      }),
    };

    // Run animation on next event loop tik
    // Because we need first return new style prop and then animate this value
    setTimeout(() => {
      animateCell({
        animationsScale,
        animationsColor,
        hasValue,
        index,
        isFocused,
      });
    }, 0);

    const isSeparator = index === 3;

    return (
      <>
        <Animated.View
          hitSlop={HIT_SLOP}
          key={index}
          style={[
            styles.cell,
            isFocused && { borderColor: theme.colors["brand.500"] },
            animatedCellStyle,
          ]}
          onLayout={getCellOnLayoutHandler(index)}
        >
          {(symbol && (
            <MainTitle
              variant="l7-semibold"
              textAlign="center"
              color="neutral.white"
            >
              {symbol}
            </MainTitle>
          )) ||
            (isFocused ? (
              <MainText
                variant="l4-medium"
                color="brand.500"
                textAlign="center"
              >
                <Cursor />
              </MainText>
            ) : null)}
        </Animated.View>
        {isSeparator && (
          <View style={styles.separatorBox}>
            <View style={styles.separator} key={`separator-${index}`} />
          </View>
        )}
      </>
    );
  };

  return (
    <CodeField
      testID="signup-magic-code-input"
      ref={ref}
      {...props}
      autoFocus
      keyboardType={Platform.OS === "ios" ? "default" : "visible-password"}
      value={value}
      autoCapitalize="characters"
      onChangeText={setValue}
      cellCount={CELL_COUNT}
      rootStyle={{ ...styles.codeFiledRoot, ...style }}
      returnKeyType="done"
      renderCell={renderCell}
    />
  );
};

export default AnimatedExample;
